import React from 'react';
import { Snackbar, Alert, TextField, Typography, Box, Grid, Button, Paper, Stack, Chip } from '@mui/material';
import '../../entrevista/components/EntrevistaViewer.css';
import { useValoracionViewer } from '../hooks/useValoracionViewer';
import PermissionGate from '../../../shared/auth/PermissionGate';

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 4,
  border: '1px solid #dbe7f3',
  background: '#ffffff',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
};

const fieldSx = (editable = false) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': {
      borderColor: editable ? '#cbd5e1' : '#d0d7de',
    },
    '&:hover fieldset': {
      borderColor: '#0d6aee',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0d6aee',
      borderWidth: 2,
    },
  },
});

const ValoracionFuncionalViewer = ({ idTipo, paciente }) => {
  const {
    patientInfo,
    fields,
    isEditing,
    openGood,
    metrics,
    setOpenGood,
    setField,
    handleEdit,
    handleCancel,
    handleSave,
    handleDelete,
    handleExportPDF,
  } = useValoracionViewer({ idTipo, paciente });

  const onFieldChange = (fieldName) => (event) => setField(fieldName, event.target.value);

  return (
    <Box className="entrevista-viewer" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box className="entrevista-viewer__shell" sx={{ maxWidth: 1080, width: '100%' }}>
        <Box id="pdf-content" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            className="entrevista-viewer__hero"
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 4,
              border: '1px solid #dbe7f3',
              background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
              boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="overline" sx={{ color: '#1d4ed8', letterSpacing: 1.4 }}>
                  Sistema de seguimiento clínico
                </Typography>
                <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
                  Valoración funcional
                </Typography>
                <Typography sx={{ color: '#475569', maxWidth: 760 }}>
                  Dinamometría de manos para monitorización de fuerza en el tiempo.
                </Typography>
              </Box>
              <Chip label={`Registro ${patientInfo.createdAt || '--/--/----'}`} variant="outlined" sx={{ borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }} />
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            {metrics.map((metric) => (
              <Grid item xs={12} md={4} key={metric.label}>
                <Paper elevation={0} sx={{ ...sectionPaperSx, p: 2.2, borderColor: '#e2e8f0', background: '#fbfdff' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                    {metric.label.toUpperCase()}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {metric.unit}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, mb: 2 }}>
              Perfil del paciente
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField label="Código" value={patientInfo.codigo} fullWidth size="small" InputProps={{ readOnly: true }} sx={fieldSx(false)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField label="Fecha" value={patientInfo.createdAt} fullWidth size="small" InputProps={{ readOnly: true }} sx={fieldSx(false)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField label="Nombre" value={patientInfo.nombre} fullWidth size="small" InputProps={{ readOnly: true }} sx={fieldSx(false)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField label="Apellidos" value={patientInfo.apellidos} fullWidth size="small" InputProps={{ readOnly: true }} sx={fieldSx(false)} />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, mb: 2 }}>
              Dinamómetro de mano
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Media derecha (kg)"
                  value={fields.mediaDerecha}
                  onChange={onFieldChange('mediaDerecha')}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx(isEditing)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Media izquierda (kg)"
                  value={fields.mediaIzquierda}
                  onChange={onFieldChange('mediaIzquierda')}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx(isEditing)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pico derecha (kg)"
                  value={fields.dsDerecha}
                  onChange={onFieldChange('dsDerecha')}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx(isEditing)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pico izquierda (kg)"
                  value={fields.dsIzquierda}
                  onChange={onFieldChange('dsIzquierda')}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx(isEditing)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Paper
          className="entrevista-viewer__actions"
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 4,
            border: '1px solid #dbe7f3',
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
            {isEditing ? (
              <>
                <Button variant="outlined" onClick={handleCancel} sx={{ borderRadius: 2.5 }}>
                  Cancelar edición
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} sx={{ borderRadius: 2.5 }}>
                  Guardar cambios
                </Button>
                <PermissionGate permission="pruebas:delete">
                  <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2.5 }}>
                    Eliminar
                  </Button>
                </PermissionGate>
              </>
            ) : (
              <>
                <Button variant="outlined" color="primary" onClick={handleEdit} sx={{ borderRadius: 2.5 }}>
                  Editar
                </Button>
                <Button variant="contained" color="secondary" onClick={handleExportPDF} sx={{ borderRadius: 2.5 }}>
                  Exportar PDF
                </Button>
                <PermissionGate permission="pruebas:delete">
                  <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2.5 }}>
                    Eliminar
                  </Button>
                </PermissionGate>
              </>
            )}
          </Stack>
        </Paper>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>¡Informe actualizado!</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ValoracionFuncionalViewer;
